import { AxiosError, AxiosResponse } from 'axios';
import { ReactNode } from 'react';
import { getApiErrorCode, getErrorMessage, getTranslatedError } from '../../apps/legacy-app/src/utils/error';

/**
 * Defines default values for API resources. Values can be overridden by specific resources in @{Resources}.
 */
const defaults: Partial<Resource> = {
  version: 'v1',
  disableDeleteConfirmation: false,
  getSuccessMessageKey(res: AxiosResponse): string | null {
    switch (res.config.method) {
      case 'post':
        return 'common.createSuccess';
      case 'put':
      case 'patch':
        return 'common.editSuccess';
      case 'delete':
        return 'common.deleteSuccess';
      default:
        return null;
    }
  },
  getErrorMessageKey(res: AxiosError): string | ReactNode | null {
    const method = res.config?.method;
    return resolveErrorCodeMessage(res) ?? (method === 'put' || method === 'patch' ? 'common.editError' : 'common.unknownError');
  },
};

/**
 * Translates a backend error `code` (with its parameters) into a display message,
 * or returns null when the response carries no code. Shared by the resource error resolvers.
 */
export function resolveErrorCodeMessage(res: AxiosError): string | ReactNode | null {
  const code = getApiErrorCode(res);
  if (!code) return null;
  const params = (res.response?.data as any)?.parameters;
  return getTranslatedError(code, params);
}

export type ExportResource = Partial<Pick<Resource, 'getSuccessMessageKey' | 'getErrorMessageKey'>>;

export interface Resource {
  path: string;
  /**
   * API version. If not provided, default value from @{defaults} is used. To disable versioning ( e.g. `/api/v1`), set it to empty string.
   */
  version: string;

  getSuccessMessageKey: (res: AxiosResponse) => string | null;
  getErrorMessageKey: (res: AxiosError) => string | ReactNode | null;

  /**
   * Translation key used when the response carries no backend error `code`.
   */
  fallbackErrorMessageKey?: string;

  disableDeleteConfirmation: boolean;

  /**
   * Optional title key for delete confirmation modal. Will be translated.
   */
  deleteConfirmationTitleKey?: string;

  /**
   * Optional message key for delete confirmation modal. Will be translated.
   */
  deleteConfirmationMessage?: string;

  /**
   * Optional button key for delete confirmation modal. Will be translated.
   * Defaults to 'common.confirmDelete.confirm'.
   */
  deleteConfirmationButtonKey?: string;

  /**
   * Optional cancel button key for delete confirmation modal. Will be translated.
   * Defaults to 'common.confirmDelete.cancel'.
   */
  deleteConfirmationCancelButtonKey?: string;

  /**
   * Optional export resource configuration. When defined, CrudService.export() will use
   * the parent resource's path + '/export' and merge these overrides.
   */
  exportResource?: ExportResource;
}

/**
 * Class to manage API resources. To add a new resource, create a new field in this class.
 *
 * Company id in path is replaced automatically if not provided in args. The only condition is that it must be named `{companyId}` in the path.
 *
 * Example:
 * ```typescript
 * nameOfTheResource: Resource = this.createResource({
 * path: '/path/to/resource',
 * version: 'v2', // optional, default values are set in @{defaults}
 * });
 * ```
 */
export class Resources {
  private createResource(config: Partial<Resource>): Resource {
    const resource = { ...defaults, ...config } as Resource;

    const fallbackErrorMessageKey = config.fallbackErrorMessageKey;
    if (fallbackErrorMessageKey && !config.getErrorMessageKey) {
      resource.getErrorMessageKey = (res: AxiosError) => {
        return resolveErrorCodeMessage(res) ?? fallbackErrorMessageKey;
      };
    }

    return resource;
  }

  getAccessPointLogs: Resource = this.createResource({
    path: '/access-points/{companyId}/logs',
  });
  accessPointValidateEntry: Resource = this.createResource({
    path: '/access-points/self-check/validate',
    getSuccessMessageKey: () => null,
  });
  readPin: Resource = this.createResource({
    path: '/companies/{companyId}/visitations/read-pin',
    getSuccessMessageKey: () => null,
  });
  visit: Resource = this.createResource({
    path: '/companies/{companyId}/visitations/visit',
    getSuccessMessageKey: () => 'common.saveSuccess',
    getErrorMessageKey: getErrorMessage,
  });
  incompleteTrainings: Resource = this.createResource({
    path: '/companies/{companyId}/visitations/trainings-incomplete',
    getSuccessMessageKey: () => null,
  });
  getWorkplaceNotificationEmployees: Resource = this.createResource({
    path: '/workplaces/notification-employees',
  });
  queryCompanies: Resource = this.createResource({
    path: '/companies',
  });
  createCompany: Resource = this.createResource({
    path: '/companies',
    getSuccessMessageKey: () => 'admin.company.addSuccess',
  });
  updateCompany: Resource = this.createResource({
    path: '/companies/{companyId}',
  });
  getCompany: Resource = this.createResource({
    path: '/companies/{companyId}',
  });
  requestCompanyDeletion: Resource = this.createResource({
    path: '/companies/{companyId}',
    disableDeleteConfirmation: true,
    getSuccessMessageKey: () => 'admin.company.deletion.requestSuccess',
  });
  cancelCompanyDeletion: Resource = this.createResource({
    path: '/companies/{companyId}/deletion-request',
    disableDeleteConfirmation: true,
    getSuccessMessageKey: () => 'admin.company.deletion.cancelSuccess',
  });
  findSupplierEmployeeInCompany: Resource = this.createResource({
    path: '/companies/{companyId}/supplier-employee',
  });
  createInvitationTypeVisit: Resource = this.createResource({
    path: '/invitation-visit',
    getSuccessMessageKey: (res: AxiosResponse) => {
      if (res.status === 200 || res.status === 201) return 'visitation.oneTimeSuccess';
      return null;
    },
  });
  requestActivity: Resource = this.createResource({
    path: '/invitations/{invitationId}/request-activity',
  });
  createEntryRequest: Resource = this.createResource({
    path: '/invitations/{invitationId}/entry-request',
  });
  updateEntryRequest: Resource = this.createResource({
    path: '/entry-request/{id}',
  });
  deleteEntryRequest: Resource = this.createResource({
    path: '/entry-request/{id}',
  });
  changeEntryRequestState: Resource = this.createResource({
    path: '/entry-request/{id}/state',
  });
  getEntryRequestsByCompany: Resource = this.createResource({
    path: '/company/{companyId}/entry-request',
  });
  getEntryRequestById: Resource = this.createResource({
    path: '/entry-request/{id}',
  });
  queryInvitations: Resource = this.createResource({
    path: '/companies/{companyId}/invitations',
  });
  getInvitationById: Resource = this.createResource({
    path: '/invitations/{invitationId}',
  });
  queryVisitations: Resource = this.createResource({
    path: '/companies/{companyId}/visitations',
    version: 'v2',
  });
  getVisitation: Resource = this.createResource({
    path: '/visitations/{visitationId}',
  });
  updateVisitation: Resource = this.createResource({
    path: '/visitations/{visitationId}',
  });
  endVisitation: Resource = this.createResource({
    path: '/visitations/{visitationId}',
    deleteConfirmationTitleKey: 'visitation.exitConfirm',
    deleteConfirmationButtonKey: 'common.yes',
    deleteConfirmationCancelButtonKey: 'common.no',
    getSuccessMessageKey: () => 'visitation.exitSuccess',
  });
  getUserVisitations: Resource = this.createResource({
    path: '/visitations/previous-visitations',
  });
  queryInvitationTypeVisit: Resource = this.createResource({
    path: '/companies/{companyId}/invitation-visit',
  });
  updateSettings: Resource = this.createResource({
    path: '/companies/{companyId}/settings',
  });
  getSettings: Resource = this.createResource({
    path: '/companies/{companyId}/settings',
  });
  getAllBySupplier: Resource = this.createResource({
    path: '/company/{companyId}/suppliers/{supplierId}/cars',
  });
  getSupplierCarById: Resource = this.createResource({
    path: '/company/{companyId}/suppliers/cars/{carId}',
  });
  createSupplierCar: Resource = this.createResource({
    path: '/suppliers/{supplierId}/cars',
  });
  updateSupplierCar: Resource = this.createResource({
    path: '/suppliers/cars/{id}',
  });
  deleteSupplierCar: Resource = this.createResource({
    path: '/suppliers/cars/{id}',
  });
  getAllCarTypesByCompany: Resource = this.createResource({
    path: '/company/{companyId}/suppliers/car-types',
  });
  createCarType: Resource = this.createResource({
    path: '/company/{companyId}/suppliers/car-types',
  });
  updateCarType: Resource = this.createResource({
    path: '/suppliers/car-types/{carTypeId}',
  });
  deleteCarType: Resource = this.createResource({
    path: '/company/{companyId}/suppliers/car-types/{carTypeId}',
  });
  getSupplierCars: Resource = this.createResource({
    path: '/company/{companyId}/suppliers/cars',
  });
  queryWorkplace: Resource = this.createResource({
    path: '/companies/{companyId}/workplaces',
  });
  createWorkplace: Resource = this.createResource({
    path: '/companies/{companyId}/workplaces',
    getSuccessMessageKey: () => 'workplace.addSuccess',
    fallbackErrorMessageKey: 'workplace.addError',
  });
  updateWorkplace: Resource = this.createResource({
    path: '/workplaces/{workplaceId}',
  });
  getWorkplace: Resource = this.createResource({
    path: '/workplaces/{workplaceId}',
  });
  addTrainingToWorkPlace: Resource = this.createResource({
    path: '/workplaces/{workplaceId}/trainings',
    getSuccessMessageKey: () => 'workplaceTraining.addSuccess',
    fallbackErrorMessageKey: 'workplaceTraining.addError',
  });
  getTrainingsByWorkplace: Resource = this.createResource({
    path: '/workplaces/{workplaceId}/trainings',
  });
  changeTrainingOrder: Resource = this.createResource({
    path: '/workplaces/{workplaceId}/trainings',
  });
  removeTrainingFromWorkplace: Resource = this.createResource({
    path: '/workplaces/{workplaceId}/trainings/{id}',
  });
  queryActivities: Resource = this.createResource({
    path: '/companies/{companyId}/activities',
  });
  createActivity: Resource = this.createResource({
    path: '/companies/{companyId}/activities',
    getSuccessMessageKey: () => 'activity.addSuccess',
    fallbackErrorMessageKey: 'activity.addError',
  });
  updateActivity: Resource = this.createResource({
    path: '/activities/{activityId}',
  });
  getActivity: Resource = this.createResource({
    path: '/activities/{activityId}',
  });
  submitApplicationInvitation: Resource = this.createResource({
    path: '/app-invitation',
    version: '',
    getSuccessMessageKey: () => 'auth.setPasswordSuccess',
  });
  resendApplicationInvitation: Resource = this.createResource({
    path: '/app-invitation/{userId}/resend',
    version: '',
    getSuccessMessageKey: () => 'user.invitationSendOk',
  });
  queryCertificates: Resource = this.createResource({
    path: '/companies/{companyId}/certificates',
  });
  createCertificate: Resource = this.createResource({
    path: '/companies/{companyId}/certificates',
    getSuccessMessageKey: () => 'document.addSuccess',
    fallbackErrorMessageKey: 'document.addError',
  });
  updateCertificate: Resource = this.createResource({
    path: '/certificates/{certificateId}',
  });
  getCertificate: Resource = this.createResource({
    path: '/certificates/{certificateId}',
  });
  queryCertificateTypes: Resource = this.createResource({
    path: '/certificates/{certificateId}/types',
  });
  createCertificateType: Resource = this.createResource({
    path: '/certificates/{certificateId}/types',
    getSuccessMessageKey: () => 'document.types.addSuccess',
    fallbackErrorMessageKey: 'document.types.addError',
  });
  updateCertificateType: Resource = this.createResource({
    path: '/certificate-types/{certificateTypeId}',
  });
  deleteCertificateType: Resource = this.createResource({
    path: '/certificate-types/{certificateTypeId}',
    deleteConfirmationMessage: 'document.types.deleteConfirm',
  });
  queryLanguages: Resource = this.createResource({
    path: '/codelists/test-languages',
  });
  createLanguage: Resource = this.createResource({
    path: '/codelists/test-languages',
    getSuccessMessageKey: () => 'admin.settings.trainingLang.addSuccess',
    fallbackErrorMessageKey: 'admin.settings.trainingLang.addError',
  });
  deleteLanguage: Resource = this.createResource({
    path: '/codelists/test-languages/{code}',
    getSuccessMessageKey: () => 'admin.settings.trainingLang.deleteSuccess',
    fallbackErrorMessageKey: 'admin.settings.trainingLang.deleteError',
  });
  updateLanguage: Resource = this.createResource({
    path: '/codelists/test-languages/{code}',
  });
  fetchSubjectDetailsFromAres: Resource = this.createResource({
    path: '/ares',
    fallbackErrorMessageKey: 'admin.company.aresError',
  });
  queryEstablishments: Resource = this.createResource({
    path: '/companies/{companyId}/establishments',
  });
  queryAllEstablishments: Resource = this.createResource({
    path: '/companies/{companyId}/allEstablishments',
  });
  createEstablishment: Resource = this.createResource({
    path: '/companies/{companyId}/establishments',
    getSuccessMessageKey: () => 'establishment.addSuccess',
  });
  updateEstablishment: Resource = this.createResource({
    path: '/establishments/{establishmentId}',
  });
  getEstablishment: Resource = this.createResource({
    path: '/establishments/{establishmentId}',
  });
  getLatestEstablishmentsUsedBy: Resource = this.createResource({
    path: '/establishments/last-used',
  });
  querySuppliers: Resource = this.createResource({
    path: '/companies/{companyId}/suppliers',
  });
  queryLendableSuppliers: Resource = this.createResource({
    path: '/companies/{companyId}/suppliers/lendable',
  });
  createSupplier: Resource = this.createResource({
    path: '/companies/{companyId}/suppliers',
    getSuccessMessageKey: () => 'supplier.addSuccess',
  });
  requestSupplier: Resource = this.createResource({
    path: '/companies/{companyId}/invitations/{invitationId}/suppliers/request',
    getSuccessMessageKey: () => 'supplierRequest.addSuccess',
  });
  approveSupplier: Resource = this.createResource({
    path: '/companies/{companyId}/suppliers/{supplierId}/approve',
    getSuccessMessageKey: () => 'supplierRequest.approveSuccess',
  });
  deleteRequestedSupplier: Resource = this.createResource({
    path: '/companies/{companyId}/suppliers/{supplierId}',
    deleteConfirmationTitleKey: 'supplierRequest.rejectTitle',
    deleteConfirmationMessage: 'supplierRequest.rejectConfirmation',
    deleteConfirmationButtonKey: 'supplierRequest.rejectAndDelete',
  });
  updateSupplier: Resource = this.createResource({
    path: '/suppliers/{supplierId}',
  });
  getSupplier: Resource = this.createResource({
    path: '/suppliers/{supplierId}',
  });
  getSupplierBasic: Resource = this.createResource({
    path: '/suppliers/{supplierId}/basic',
  });
  querySupplierEmployees: Resource = this.createResource({
    path: '/suppliers/{supplierId}/employees',
  });
  querySupplierEmployeesByCompany: Resource = this.createResource({
    path: '/company/{companyId}/supplier-employees',
  });
  createSupplierEmployee: Resource = this.createResource({
    path: '/suppliers/{supplierId}/employees',
  });
  updateVisitorToSupplierEmployee: Resource = this.createResource({
    path: '/supplier-employees/{employeeId}/assignToSupplier',
  });
  updateSupplierEmployee: Resource = this.createResource({
    path: '/supplier-employees/{employeeId}',
  });
  getSupplierEmployee: Resource = this.createResource({
    path: '/supplier-employees/{employeeId}',
  });
  getSupplierEmployeeProtocol: Resource = this.createResource({
    path: '/supplier-employees/{employeeId}/protocol',
  });
  revealPin: Resource = this.createResource({
    path: '/suppliers-employees/{employeeId}/reveal-pin',
  });
  getCertificates: Resource = this.createResource({
    path: '/supplier-employees/{employeeId}/certificates',
  });
  getSupplierFromUserId: Resource = this.createResource({
    path: '/supplier-employees-as-user/{userId}/supplier',
  });
  getInvitationsData: Resource = this.createResource({
    path: '/supplier-employees/{employeeId}/invitations-data',
  });
  getTrainingVersion: Resource = this.createResource({
    path: '/training-versions/{versionId}',
  });
  queryTrainingVersions: Resource = this.createResource({
    path: '/trainings/{trainingId}/versions',
  });
  createTrainingVersion: Resource = this.createResource({
    path: '/trainings/{trainingId}/versions',
    getSuccessMessageKey: () => 'training.addVersionSuccess',
    fallbackErrorMessageKey: 'training.addVersionError',
  });
  updateTrainingVersion: Resource = this.createResource({
    path: '/training-versions/{versionId}',
  });
  deleteTrainingVersion: Resource = this.createResource({
    path: '/training-versions/{versionId}',
    getSuccessMessageKey: () => 'training.versionDeleted',
    fallbackErrorMessageKey: 'training.versionDeleteError',
    deleteConfirmationTitleKey: 'training.askDeleteVersion',
  });
  addTVLanguageVariant: Resource = this.createResource({
    path: '/training-versions/{versionId}/languages',
    getSuccessMessageKey: () => 'training.addLanguageSuccess',
    fallbackErrorMessageKey: 'training.addLanguageError',
  });
  copyTVLanguageVariant: Resource = this.createResource({
    path: '/training-versions/{versionId}/languages/{language}',
    getSuccessMessageKey: () => 'training.copy.success',
    fallbackErrorMessageKey: 'training.copy.error',
  });
  deleteTVLanguageVariant: Resource = this.createResource({
    path: '/training-versions/{versionId}/slides/{language}',
    getSuccessMessageKey: () => 'training.slideDeleted',
    fallbackErrorMessageKey: 'training.slideDeleteError',
    deleteConfirmationTitleKey: 'training.askDeleteLanguage',
  });
  getSlides: Resource = this.createResource({
    path: '/training-versions/{versionId}/slides/{language}',
  });
  createSlide: Resource = this.createResource({
    path: '/training-versions/{versionId}/slides',
  });
  updateSlide: Resource = this.createResource({
    path: '/slides/{slide}',
  });
  deleteSlide: Resource = this.createResource({
    path: '/slides/{slide}',
  });
  changeSlideOrder: Resource = this.createResource({
    path: '/training-versions/{versionId}/order',
  });
  copyTrainingVersion: Resource = this.createResource({
    path: '/training-versions/{versionId}',
    getSuccessMessageKey: () => 'training.copy.success',
    fallbackErrorMessageKey: 'training.copy.error',
  });
  copyVersionToTrainings: Resource = this.createResource({
    path: '/training-versions/{versionId}/copy',
    getSuccessMessageKey: () => 'training.copy.success',
    fallbackErrorMessageKey: 'training.copy.error',
  });
  copyVersionToNewTraining: Resource = this.createResource({
    path: '/training-versions/{versionId}/new',
    getSuccessMessageKey: () => 'training.copy.success',
    fallbackErrorMessageKey: 'training.copy.error',
  });
  getCurrentQuestionnaireVersion: Resource = this.createResource({
    path: '/questionnaires/{questionnaireId}/languages/{lang}',
  });
  queryTrainings: Resource = this.createResource({
    path: '/companies/{companyId}/trainings',
  });
  createTraining: Resource = this.createResource({
    path: '/companies/{companyId}/trainings',
  });
  getTrainingById: Resource = this.createResource({
    path: '/trainings/{trainingId}',
  });
  updateTraining: Resource = this.createResource({
    path: '/trainings/{trainingId}',
  });
  submitTrainingAnswers: Resource = this.createResource({
    path: '/training-versions/{trainingVersionId}/submit-answer',
    getSuccessMessageKey: () => null,
  });
  submitPlaygroundAnswers: Resource = this.createResource({
    path: '/questionnaire-version/submit-playground-answer',
    getSuccessMessageKey: () => null,
  });
  signTraining: Resource = this.createResource({
    path: '/training-versions/{trainingVersionId}/sign',
  });
  queryQuestionnaires: Resource = this.createResource({
    path: '/companies/{companyId}/questionnaires',
  });
  createQuestionnaire: Resource = this.createResource({
    path: '/companies/{companyId}/questionnaires',
    getSuccessMessageKey: () => 'questionnaire.addSuccess',
    fallbackErrorMessageKey: 'questionnaire.addError',
  });
  updateQuestionnaire: Resource = this.createResource({
    path: '/questionnaire/{questionnaireId}',
    getSuccessMessageKey: () => 'questionnaire.updateSuccess',
  });
  getQuestionnaireById: Resource = this.createResource({
    path: '/questionnaires/{questionnaireId}',
  });
  queryQuestionnaireVersions: Resource = this.createResource({
    path: '/questionnaires/{questionnaireId}/versions',
  });
  createQuestionnaireVersion: Resource = this.createResource({
    path: '/questionnaires/{questionnaireId}/versions',
    getSuccessMessageKey: () => 'training.addVersionSuccess',
    fallbackErrorMessageKey: 'training.addVersionError',
  });
  getQuestionnaireVersion: Resource = this.createResource({
    path: '/questionnaire-version/{versionId}',
  });
  updateQuestionnaireVersion: Resource = this.createResource({
    path: '/questionnaire-version/{versionId}',
  });
  deleteQuestionnaireVersion: Resource = this.createResource({
    path: '/questionnaire-versions/{versionId}',
    getSuccessMessageKey: () => 'questionnaire.versionDeleted',
    fallbackErrorMessageKey: 'questionnaire.deleteVersionError',
    deleteConfirmationTitleKey: 'questionnaire.askDeleteVersion',
  });
  addLanguageForVersion: Resource = this.createResource({
    path: '/questionnaire-version/{versionId}',
    getSuccessMessageKey: () => 'questionnaire.addLanguageSuccess',
    fallbackErrorMessageKey: 'questionnaire.addLanguageError',
  });
  createCopyFromLanguageVersion: Resource = this.createResource({
    path: '/questionnaire-version/{versionId}/languages/{language}',
    getSuccessMessageKey: () => 'questionnaire.copy.success',
    fallbackErrorMessageKey: 'questionnaire.copy.error',
  });
  deleteQVLanguageVariant: Resource = this.createResource({
    path: '/questionnaire-version/{versionId}/questions/{language}',
    getSuccessMessageKey: () => 'training.slideDeleted',
    fallbackErrorMessageKey: 'training.slideDeleteError',
    deleteConfirmationTitleKey: 'training.askDeleteLanguage',
  });
  getQuestions: Resource = this.createResource({
    path: '/questionnaire-version/{versionId}/questions/{language}',
  });
  updateAllQuestions: Resource = this.createResource({
    path: '/questionnaire-version/{versionId}/questions/{language}',
    getSuccessMessageKey: () => 'questionnaire.questions.saveSuccess',
    fallbackErrorMessageKey: 'questionnaire.questions.saveError',
  });
  copyQuestionnaireVersion: Resource = this.createResource({
    path: '/questionnaire-versions/{versionId}',
    getSuccessMessageKey: () => 'questionnaire.copy.success',
    fallbackErrorMessageKey: 'questionnaire.copy.error',
  });
  copyVersionToQuestionnaires: Resource = this.createResource({
    path: '/questionnaire-versions/{versionId}/copy',
    getSuccessMessageKey: () => 'questionnaire.questions.saveSuccess',
    fallbackErrorMessageKey: 'questionnaire.questions.saveError',
  });
  copyVersionToNewQuestionnaire: Resource = this.createResource({
    path: '/questionnaire-versions/{versionId}/new',
    getSuccessMessageKey: () => 'questionnaire.copy.success',
    fallbackErrorMessageKey: 'questionnaire.copy.success',
  });
  updateDocument: Resource = this.createResource({
    path: '/invitations/{invitationId}/documents/{certificateId}',
  });
  approveDocumentCertificate: Resource = this.createResource({
    path: '/invitations/{invitationId}/documents',
  });
  createInvitation: Resource = this.createResource({
    path: '/companies/{companyId}/invitations',
    getSuccessMessageKey: () => 'invitation.create.success',
  });
  updateInvitation: Resource = this.createResource({
    path: '/invitations/{invitationId}',
  });
  unarchiveInvitation: Resource = this.createResource({
    path: '/invitations/{invitationId}/unarchive',
    getSuccessMessageKey: () => 'invitation.unarchiveSuccess',
    fallbackErrorMessageKey: 'invitation.unarchiveError',
  });
  deleteInvitation: Resource = this.createResource({
    path: '/invitations/{invitationId}',
    getSuccessMessageKey: () => 'invitation.archiveSuccess',
    fallbackErrorMessageKey: 'invitation.archiveError',
    deleteConfirmationTitleKey: 'invitation.askToArchive',
    deleteConfirmationButtonKey: 'invitation.archive',
  });
  confirmInvitationDetails: Resource = this.createResource({
    path: '/invitations/{invitationId}/details',
  });
  addAttachment: Resource = this.createResource({
    path: '/invitations/{invitationId}/attachments',
    getSuccessMessageKey: () => 'attachment.uploadSuccess',
  });
  deleteAttachments: Resource = this.createResource({
    path: '/invitations/{invitationId}/attachments',
    disableDeleteConfirmation: true,
  });
  updatePerson: Resource = this.createResource({
    path: '/invitations/persons/{personId}',
  });
  overridePersonRequirements: Resource = this.createResource({
    path: '/invitations/{invitationId}/persons/{personId}/override',
  });
  resendNotification: Resource = this.createResource({
    path: '/invitations/{invitationId}/persons/{personId}/resend',
    getSuccessMessageKey: () => 'invitation.persons.resendNotificationSuccess',
  });
  getInvitationAudit: Resource = this.createResource({
    path: '/invitations/{invitationId}/audit',
  });
  queryPersons: Resource = this.createResource({
    path: '/invitations/{invitationId}/persons',
  });
  addPerson: Resource = this.createResource({
    path: '/invitations/{invitationId}/persons',
  });
  deletePerson: Resource = this.createResource({
    path: '/invitations/persons/{personId}',
    getSuccessMessageKey: () => 'invitation.persons.deleteSuccess',
    fallbackErrorMessageKey: 'invitation.persons.deleteError',
    deleteConfirmationTitleKey: 'invitation.tabs.askDeletePerson',
    deleteConfirmationButtonKey: 'common.remove',
  });
  bulkUpdatePersons: Resource = this.createResource({
    path: '/invitations/{invitationId}/persons/bulk-update',
  });
  addOneTimePerson: Resource = this.createResource({
    path: '/invitations/{invitationId}/one-time-persons',
  });
  approveRisks: Resource = this.createResource({
    path: '/invitations/{invitationId}/approve-risks',
    getSuccessMessageKey: () => 'invitation.workplace.approveRisksSuccess',
  });
  approveActivities: Resource = this.createResource({
    path: '/invitations/{invitationId}/approve-activities',
    getSuccessMessageKey: () => 'invitation.activity.approveSuccess',
  });
  addDocumentsForActivities: Resource = this.createResource({
    path: '/invitations/{invitationId}/activity-documents',
  });
  generateZipFile: Resource = this.createResource({
    path: '/invitations/{invitationId}/related-files',
  });
  getInvitationHistory: Resource = this.createResource({
    path: '/invitations/{invitationId}/history',
  });
  getInvitationHistoryPersons: Resource = this.createResource({
    path: '/invitations/{invitationId}/history/persons',
  });
  sendInvitationNotification: Resource = this.createResource({
    path: '/invitations/{invitationId}/send-notification',
    getSuccessMessageKey: () => 'invitation.notificationSentSuccess',
  });
  getBusinessSettings: Resource = this.createResource({
    path: '/companies/{companyId}/business-settings',
  });
  updateBusinessSettings: Resource = this.createResource({
    path: '/companies/{companyId}/business-settings',
  });
  checkOptions: Resource = this.createResource({
    path: '/companies/{companyId}/check',
  });
  getWorkplaceHandoverSettings: Resource = this.createResource({
    path: '/companies/{companyId}/settings/workplace-handover',
  });
  updateWorkplaceHandoverSettings: Resource = this.createResource({
    path: '/companies/{companyId}/settings/workplace-handover',
    getSuccessMessageKey: () => 'handoverSettings.settingsSaved',
  });
  getWorkplaceHandoverSections: Resource = this.createResource({
    path: '/companies/{companyId}/settings/workplace-handover/sections',
  });
  addWorkplaceHandoverSection: Resource = this.createResource({
    path: '/companies/{companyId}/settings/workplace-handover/sections',
    getSuccessMessageKey: () => 'common.saveSuccess',
  });
  updateWorkplaceHandoverSections: Resource = this.createResource({
    path: '/companies/{companyId}/settings/workplace-handover/sections',
    getSuccessMessageKey: () => 'common.saveSuccess',
  });
  previewWorkplaceHandover: Resource = this.createResource({
    path: '/companies/{companyId}/workplace-handover/preview',
  });
  updateWorkplaceHandover: Resource = this.createResource({
    path: '/invitations/{invitationId}/workplace-handover',
    getSuccessMessageKey: () => 'invitation.workplaceHandoverSaveSuccess',
  });
  exportWorkplaceHandover: Resource = this.createResource({
    path: '/invitations/{invitationId}/workplace-handover/export',
    getSuccessMessageKey: () => 'common.exportSuccess',
  });
  uploadWorkplaceHandoverDocument: Resource = this.createResource({
    path: '/invitations/{invitationId}/workplace-handover/documents',
    getSuccessMessageKey: () => 'invitation.uploadSuccess',
  });
  uploadSignatures: Resource = this.createResource({
    path: '/invitations/{invitationId}/workplace-handover/sign',
    getSuccessMessageKey: () => 'invitation.uploadSuccess',
  });
  queryEmployees: Resource = this.createResource({
    path: '/companies/{companyId}/employees',
  });
  createEmployee: Resource = this.createResource({
    path: '/companies/{companyId}/employees',
    getSuccessMessageKey: () => 'employee.addSuccess',
  });
  updateEmployee: Resource = this.createResource({
    path: '/employees/{employeeId}',
  });
  getEmployee: Resource = this.createResource({
    path: '/employees/{employeeId}',
  });
  createUser: Resource = this.createResource({
    path: '/employees/{employeeId}/users',
    getSuccessMessageKey: () => 'employee.createUserSuccess',
  });
  importEmployees: Resource = this.createResource({
    path: '/companies/{companyId}/import',
    getSuccessMessageKey: () => 'employee.importSuccess',
  });
  exportEmployees: Resource = this.createResource({
    path: '/companies/{companyId}/export',
    fallbackErrorMessageKey: 'employee.exportError',
  });
  queryQcsCoreUsers: Resource = this.createResource({
    path: '/users',
  });
  createQcsUser: Resource = this.createResource({
    path: '/users',
    getSuccessMessageKey: () => 'admin.user.addError',
    fallbackErrorMessageKey: 'admin.user.addError',
  });
  updateUser: Resource = this.createResource({
    path: '/users/{userId}',
  });
  getUser: Resource = this.createResource({
    path: '/users/{userId}',
  });
  queryCompanyUsers: Resource = this.createResource({
    path: '/companies/{companyId}/users',
  });
  updateUserPreferences: Resource = this.createResource({
    path: '/users/{userId}/preferences',
  });
  getUserPreferences: Resource = this.createResource({
    path: '/users/{userId}/preferences',
  });
  queryCompanyPages: Resource = this.createResource({
    path: '/companies/{companyId}/pages',
  });
  updateCompanyPage: Resource = this.createResource({
    path: '/companies/{companyId}/pages',
  });
  visitationsAnalytics: Resource = this.createResource({
    path: '/companies/{companyId}/analytics/visitations',
  });
  visitationsAnalyticsExport: Resource = this.createResource({
    path: '/companies/{companyId}/analytics/visitations/export',
  });

  // ---- dashboard tiles (personal) ------------------------------------
  listTiles: Resource = this.createResource({
    path: '/dashboard/tiles',
  });
  createTile: Resource = this.createResource({
    path: '/dashboard/tiles',
    getSuccessMessageKey: () => 'analytics.tile.created',
  });
  updateTile: Resource = this.createResource({
    path: '/dashboard/tiles/{tileId}',
    getSuccessMessageKey: () => 'analytics.tile.updated',
  });
  deleteTile: Resource = this.createResource({
    path: '/dashboard/tiles/{tileId}',
    getSuccessMessageKey: () => 'analytics.tile.deleted',
    deleteConfirmationTitleKey: 'analytics.tile.deleteTitle',
  });
  reorderTiles: Resource = this.createResource({
    path: '/dashboard/tiles/order',
    fallbackErrorMessageKey: 'analytics.tile.reorderFailed',
  });

  // ---- dashboard tiles (company defaults) ----------------------------
  listCompanyDefaults: Resource = this.createResource({
    path: '/companies/{companyId}/dashboard/defaults',
  });
  createCompanyDefault: Resource = this.createResource({
    path: '/companies/{companyId}/dashboard/defaults',
    getSuccessMessageKey: () => 'analytics.tile.created',
  });
  updateCompanyDefault: Resource = this.createResource({
    path: '/companies/{companyId}/dashboard/defaults/{tileId}',
    getSuccessMessageKey: () => 'analytics.tile.updated',
  });
  deleteCompanyDefault: Resource = this.createResource({
    path: '/companies/{companyId}/dashboard/defaults/{tileId}',
    getSuccessMessageKey: () => 'analytics.tile.deleted',
    deleteConfirmationTitleKey: 'analytics.tile.deleteTitle',
  });

  // ---- dashboard tiles (global defaults) -----------------------------
  listGlobalDefaults: Resource = this.createResource({
    path: '/dashboard/defaults',
  });
  createGlobalDefault: Resource = this.createResource({
    path: '/dashboard/defaults',
    getSuccessMessageKey: () => 'analytics.tile.created',
  });
  updateGlobalDefault: Resource = this.createResource({
    path: '/dashboard/defaults/{tileId}',
    getSuccessMessageKey: () => 'analytics.tile.updated',
  });
  deleteGlobalDefault: Resource = this.createResource({
    path: '/dashboard/defaults/{tileId}',
    getSuccessMessageKey: () => 'analytics.tile.deleted',
    deleteConfirmationTitleKey: 'analytics.tile.deleteTitle',
  });
}

export const resources = new Resources();
